/**
 * assets/fa-reviews.js
 * Flooring Attic review widget — half-star picker, recommend toggle,
 * customer photo gallery + lightbox, persistent helpful votes, color swatch picker.
 */
(function () {
  "use strict";

  const API_ENDPOINT = document.querySelector(".fa-reviews")?.dataset.apiEndpoint
    || "https://afdc-product-editor.onrender.com/api/reviews";

  const STAGE_VIDEO_ENDPOINT = API_ENDPOINT.replace("/api/reviews", "/api/reviews/stage-video");
  const HALF_LABELS = ["","Poor","Poor","Fair","Fair","Good","Good","Very good","Very good","Excellent","Excellent"];
  const MAX_PHOTOS  = 4;

  let allReviews    = [];
  let currentFilter = "all";
  let currentSort   = "helpful";
  let currentPage   = 1;
  let selectedPhotos = [];
  let selectedVideo  = null; // { file, dataUrl, blobUrl }
  let currentRating  = 0;
  let recommendValue = "";
  let colorValue     = "";
  let lightboxPhotos = [];
  let lightboxIdx    = 0;

  let reviewPhotoGroups = [];
  let rlGroupIdx = 0;
  let rlPhotoIdx = 0;

  const VOTES_KEY = "fa_helpful_votes";
  let votedIds = {};
  try { votedIds = JSON.parse(localStorage.getItem(VOTES_KEY) || "{}"); } catch {}
  function saveVotes() {
    try { localStorage.setItem(VOTES_KEY, JSON.stringify(votedIds)); } catch {}
  }

  // ── Image compression ────────────────────────────────────────────────────
  function compressImage(dataUrl, maxWidth, quality) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        let w = img.width, h = img.height;
        if (w > maxWidth) { h = Math.round(h * maxWidth / w); w = maxWidth; }
        canvas.width = w; canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL("image/jpeg", quality));
      };
      img.onerror = reject;
      img.src = dataUrl;
    });
  }

  // ── Init ──────────────────────────────────────────────────────────────────
  function init() {
    const dataEl = document.getElementById("fa-reviews-data");
    if (dataEl) {
      try { allReviews = JSON.parse(dataEl.textContent); } catch {}
      buildPhotoGallery();
      renderList();
      bindFilters();
      bindSort();
    }
    buildPdpStarBadge();
    bindWriteButton();
    bindOverlay();
    buildHalfStarPicker();
    buildColorField();
    bindRecommend();
    bindChips();
    bindPhotoUpload();
    bindVideoUpload();
    bindForm();
    bindLightbox();
    bindReviewLightbox();
    buildVideoModal();

    // Delegated handler — swap video poster for inline iframe on click, everywhere
    document.addEventListener("click", function(e) {
      const poster = e.target.closest(".fa-reviews__video-poster");
      if (!poster) return;
      e.preventDefault();
      const src = poster.dataset.embedSrc;
      if (!src) return;
      const wrap = poster.closest(".fa-reviews__video-wrap, .fa-rlightbox__video");
      if (!wrap) return;
      const iframe = document.createElement("iframe");
      iframe.className = "fa-reviews__video-embed";
      iframe.src = src;
      iframe.setAttribute("frameborder", "0");
      iframe.setAttribute("allow", "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture");
      iframe.setAttribute("allowfullscreen", "");
      iframe.style.cssText = "position:absolute;inset:0;width:100%;height:100%;border:none;";
      poster.replaceWith(iframe);
    });
    document.addEventListener("keydown", function(e) {
      if (e.key === "Enter" || e.key === " ") {
        const poster = e.target.closest(".fa-reviews__video-poster");
        if (poster) { e.preventDefault(); poster.click(); }
      }
    });
    bindFlagModal();
    initScrollAnimations();
  }

  // ── PDP star badge ────────────────────────────────────────────────────────
  function buildPdpStarBadge() {
    const header = document.querySelector(".fa-pdp__gallery-header");
    if (!header) return;

    // ── Score calculation (same integer math as Liquid) ──────────────────
    const count = allReviews.length;
    let total = 0;
    allReviews.forEach(function(r) { total += (r.rating || 0); });
    const scoreInt   = count > 0 ? Math.floor(total * 10 / count) : 0;
    const scoreWhole = Math.floor(scoreInt / 10);
    const scoreDec   = scoreInt % 10;
    const scoreStr   = count > 0 ? (scoreWhole + "." + scoreDec) : "";

    // ── Star SVGs ────────────────────────────────────────────────────────
    const PATH = "M10 1l2.39 4.84 5.35.78-3.87 3.77.91 5.32L10 13.27l-4.78 2.44.91-5.32L2.26 6.62l5.35-.78L10 1z";
    const showHalf = scoreDec >= 5;
    const halfPos  = scoreWhole + 1;
    let starsMarkup = "";
    for (let i = 1; i <= 5; i++) {
      if (i <= scoreWhole) {
        starsMarkup += `<svg class="fa-star fa-star--full" width="16" height="16" viewBox="0 0 20 20" aria-hidden="true"><path d="${PATH}"/></svg>`;
      } else if (i === halfPos && showHalf && count > 0) {
        starsMarkup += `<svg class="fa-star fa-star--half" width="16" height="16" viewBox="0 0 20 20" aria-hidden="true" style="overflow:visible">
          <defs><clipPath id="fa-badge-half"><rect x="0" y="0" width="10" height="20"/></clipPath></defs>
          <path d="${PATH}" fill="none" stroke="rgba(0,0,0,0.22)" stroke-width="1.5"/>
          <path d="${PATH}" fill="#d22129" clip-path="url(#fa-badge-half)"/>
        </svg>`;
      } else {
        starsMarkup += `<svg class="fa-star fa-star--empty" width="16" height="16" viewBox="0 0 20 20" aria-hidden="true"><path d="${PATH}" fill="none" stroke="rgba(0,0,0,0.22)" stroke-width="1.5"/></svg>`;
      }
    }

    // ── Badge element ────────────────────────────────────────────────────
    const badge = document.createElement("a");
    badge.id    = "fa-pdp-badge-wrap";
    badge.href  = "#fa-reviews";
    badge.setAttribute("aria-label", count > 0 ? `${scoreStr} out of 5 stars, ${count} reviews` : "No reviews yet");
    badge.innerHTML =
      `<div class="fa-pdp-badge__stars">${starsMarkup}</div>` +
      (count > 0
        ? `<span class="fa-pdp-badge__score">${scoreStr}</span><span class="fa-pdp-badge__count">(${count})</span>`
        : "");

    // ── Layout: flex, breadcrumb stretches full height, badge right ──────
    header.style.cssText = "display:flex;align-items:center;justify-content:space-between;min-height:26px;padding:0;";
    const breadcrumb = header.querySelector(".fa-pdp__breadcrumb");
    if (breadcrumb) breadcrumb.style.cssText = "display:flex;align-items:center;align-self:stretch;";
    header.appendChild(badge);

    // ── Move .pdp-actions (share/print) above the SKU number ────────────
    const pdpActions = document.querySelector(".pdp-actions");
    const skuWrap    = document.querySelector(".fa-pdp__sku-wrap");
    if (pdpActions && skuWrap) {
      skuWrap.parentElement.insertBefore(pdpActions, skuWrap);
      pdpActions.style.cssText = "display:flex;align-items:center;gap:2px;margin-bottom:4px;";
      Array.from(pdpActions.children).forEach(function(btn) {
        btn.style.cssText = "background:none;border:none;cursor:pointer;padding:3px 5px;display:flex;align-items:center;color:rgba(0,0,0,0.4);transition:color 0.15s;";
        btn.addEventListener("mouseenter", function() { btn.style.color = "rgba(0,0,0,0.81)"; });
        btn.addEventListener("mouseleave", function() { btn.style.color = "rgba(0,0,0,0.4)"; });
      });
      // Reveal now that it's in the right place
      pdpActions.style.visibility = "visible";
      // Balance icon sizes — share viewBox is ~89x91, print is 24x24
      pdpActions.querySelectorAll("button svg").forEach(function(svg) {
        const isShare = (svg.getAttribute("viewBox") || "").includes("89");
        const size = isShare ? "20" : "19";
        svg.setAttribute("width", size);
        svg.setAttribute("height", size);
        svg.style.cssText = "width:" + size + "px;height:" + size + "px;display:block;flex-shrink:0;";
        svg.setAttribute("preserveAspectRatio", "xMidYMid meet");
      });
    }
  }

  // ── Color swatch picker ───────────────────────────────────────────────────
  function buildColorField() {
    const section = document.getElementById("fa-color-section");
    if (!section) return;

    const widget = document.getElementById("fa-reviews");
    if (!widget) return;

    let variants = [];
    try { variants = JSON.parse(widget.dataset.variants || "[]"); } catch {}
    if (!variants.length) return;

    const selectedId = widget.dataset.selectedVariantId;
    const isSingle   = variants.length === 1;

    function getColorTitle(v) {
      if (v.title && v.title !== "Default Title") return v.title;
      const productTitle = widget.dataset.productTitle || "";
      // Try to extract color after common separators: " - ", " — ", " – "
      const sepMatch = productTitle.match(/[—–-]\s*(.+)$/);
      if (sepMatch) return sepMatch[1].trim();
      // Fall back to last segment after " / "
      const parts = productTitle.split(" / ");
      return parts.length > 1 ? parts[parts.length - 1].trim() : productTitle;
    }

    const selectedVariant = variants.find(v => String(v.id) === String(selectedId)) || variants[0];
    colorValue = isSingle ? getColorTitle(selectedVariant) : (selectedVariant.title || "");

    if (isSingle) {
      section.innerHTML = `
        <div class="fa-overlay__section-label">Color <span class="fa-opt">(auto-filled)</span></div>
        <div class="fa-color-swatches">
          <div class="fa-color-swatch-wrap">
            <div class="fa-color-swatch is-selected is-single" aria-label="${esc(colorValue)}" title="${esc(colorValue)}">
              <img src="${esc(selectedVariant.image)}" alt="${esc(colorValue)}" loading="lazy">
            </div>
            <span class="fa-color-swatch-label">${esc(colorValue)}</span>
          </div>
        </div>
        <input type="hidden" id="fa-color" value="${esc(colorValue)}">
      `;
    } else {
      const swatchesHTML = variants.map(v => {
        const isSelected = String(v.id) === String(selectedId);
        const colorName  = v.title || "";
        if (isSelected) colorValue = colorName;
        return `
          <div class="fa-color-swatch-wrap">
            <button type="button"
              class="fa-color-swatch${isSelected ? " is-selected" : ""}"
              data-color-name="${esc(colorName)}"
              title="${esc(colorName)}"
              aria-label="${esc(colorName)}"
              aria-pressed="${isSelected}">
              <img src="${esc(v.image)}" alt="${esc(colorName)}" loading="lazy">
            </button>
            <span class="fa-color-swatch-label">${esc(colorName)}</span>
          </div>`;
      }).join("");

      section.innerHTML = `
        <div class="fa-overlay__section-label">Color <span class="fa-req">*</span></div>
        <div class="fa-color-swatches" id="fa-color-swatches">${swatchesHTML}</div>
        <input type="hidden" id="fa-color" value="${esc(colorValue)}">
      `;

      section.querySelectorAll(".fa-color-swatch").forEach(btn => {
        btn.addEventListener("click", () => {
          section.querySelectorAll(".fa-color-swatch").forEach(b => {
            b.classList.remove("is-selected");
            b.setAttribute("aria-pressed", "false");
          });
          btn.classList.add("is-selected");
          btn.setAttribute("aria-pressed", "true");
          colorValue = btn.dataset.colorName;
          const hidden = document.getElementById("fa-color");
          if (hidden) hidden.value = colorValue;
          // Clear error when selection made
          const colorErr = document.getElementById("fa-color-error");
          if (colorErr) colorErr.hidden = true;
        });
      });
    }

    // Append error element after innerHTML is set
    if (!section.querySelector("#fa-color-error")) {
      const err = document.createElement("div");
      err.className = "fa-overlay__field-error";
      err.id = "fa-color-error";
      err.hidden = true;
      err.textContent = "Please select a color.";
      section.appendChild(err);
    }
  }

  // ── Scroll animations ─────────────────────────────────────────────────────
  function initScrollAnimations() {
    const summary = document.querySelector(".fa-reviews__summary");
    if (!summary || typeof IntersectionObserver === "undefined") return;

    const stars        = document.querySelectorAll(".fa-reviews__stars .fa-star");
    const barFills     = document.querySelectorAll(".fa-reviews__bar-fill");
    const scoreEl      = document.querySelector(".fa-reviews__score-num");
    const recommendEls = document.querySelectorAll(".fa-reviews__recommend-num");

    const realScore     = scoreEl ? parseFloat(scoreEl.textContent) : 0;
    const realRecommend = recommendEls.length ? parseInt(recommendEls[0].textContent) : 0;
    const realWidths    = Array.from(barFills).map(b => b.style.width);

    stars.forEach(s => {
      s.style.opacity    = "0";
      s.style.transform  = "scale(0.3)";
      s.style.transition = "opacity 0.35s ease, transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1)";
    });
    barFills.forEach(b => { b.style.transition = "none"; b.style.width = "0%"; });
    if (scoreEl) scoreEl.textContent = "0.0";
    recommendEls.forEach(el => { el.textContent = "0%"; });

    function animateCount(el, from, to, duration, decimals, suffix) {
      var start = performance.now();
      function step(now) {
        var progress = Math.min((now - start) / duration, 1);
        var ease = 1 - Math.pow(1 - progress, 3);
        el.textContent = (from + (to - from) * ease).toFixed(decimals) + (suffix || "");
        if (progress < 1) requestAnimationFrame(step);
      }
      requestAnimationFrame(step);
    }

    var hasAnimated = false;
    var observer = new IntersectionObserver(function(entries) {
      entries.forEach(function(entry) {
        if (entry.isIntersecting && !hasAnimated) {
          hasAnimated = true;
          observer.disconnect();
          stars.forEach(function(star, i) {
            setTimeout(function() { star.style.opacity = "1"; star.style.transform = "scale(1)"; }, i * 150);
          });
          barFills.forEach(function(bar, i) {
            setTimeout(function() {
              bar.style.transition = "width 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)";
              bar.style.width = realWidths[i] || "0%";
            }, 250 + i * 120);
          });
          if (scoreEl) animateCount(scoreEl, 0, realScore, 1000, 1);
          if (realRecommend > 0) {
            setTimeout(function() {
              recommendEls.forEach(function(el) { animateCount(el, 0, realRecommend, 1000, 0, "%"); });
            }, 350);
          }
        }
      });
    }, { threshold: 0.2 });
    observer.observe(summary);
  }

  // ── Photo gallery ─────────────────────────────────────────────────────────
  function buildPhotoGallery() {
    const gallery = document.getElementById("fa-photo-gallery");
    lightboxPhotos    = [];
    reviewPhotoGroups = [];

    allReviews.forEach(r => {
      // Build unified items array: photos first, then video if present
      let photos = [];
      try {
        const raw = r.photos;
        if (Array.isArray(raw)) photos = raw.filter(s => typeof s === "string" && s.length > 0);
        else if (typeof raw === "string" && raw.startsWith("[")) photos = JSON.parse(raw).filter(s => typeof s === "string" && s.length > 0);
      } catch {}

      const items = [
        ...photos.map(src => ({ type: "photo", src })),
        ...(r.video_url ? [{ type: "video", src: r.video_url }] : []),
      ];

      if (items.length === 0) return;

      const groupIdx = reviewPhotoGroups.length;
      items.forEach((item, itemIdx) => {
        lightboxPhotos.push({ ...item, author: r.author_name, groupIdx, photoIdx: itemIdx });
      });
      reviewPhotoGroups.push({ review: r, photos: items });
    });

    if (!gallery || lightboxPhotos.length === 0) return;
    gallery.style.display = "";
    gallery.innerHTML = `
      <div class="fa-reviews__gallery-title">Customer Photos & Videos</div>
      <div class="fa-reviews__gallery-grid">
        ${lightboxPhotos.map((p) => {
          if (p.type === "video") {
            const thumb = getVideoThumbnail(p.src);
            return `<button class="fa-reviews__gallery-thumb fa-reviews__gallery-thumb--video" data-group-idx="${p.groupIdx}" data-photo-idx="${p.photoIdx}" aria-label="View video by ${esc(p.author)}">
              ${thumb ? `<img src="${thumb}" alt="Video by ${esc(p.author)}" loading="lazy">` : `<div class="fa-reviews__gallery-thumb-placeholder"></div>`}
              <span class="fa-reviews__gallery-play">▶</span>
            </button>`;
          }
          return `<button class="fa-reviews__gallery-thumb" data-group-idx="${p.groupIdx}" data-photo-idx="${p.photoIdx}" aria-label="View photo by ${esc(p.author)}">
            <img src="${p.src}" alt="Customer photo by ${esc(p.author)}" loading="lazy">
          </button>`;
        }).join("")}
      </div>`;
    gallery.querySelectorAll(".fa-reviews__gallery-thumb").forEach(btn => {
      btn.addEventListener("click", () => openReviewLightbox(parseInt(btn.dataset.groupIdx), parseInt(btn.dataset.photoIdx)));
    });
  }

  // ── Get YouTube/Vimeo thumbnail URL ───────────────────────────────────────
  function getVideoThumbnail(url) {
    const ytMatch = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/);
    if (ytMatch) return `https://img.youtube.com/vi/${ytMatch[1]}/mqdefault.jpg`;
    const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
    if (vimeoMatch) return null;
    return null;
  }

  function getYouTubeEmbedUrl(url) {
    const ytMatch = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/);
    if (ytMatch) return `https://www.youtube-nocookie.com/embed/${ytMatch[1]}`;
    return url;
  }

  // ── Lightbox ──────────────────────────────────────────────────────────────
  function bindLightbox() {
    document.getElementById("fa-lightbox-close")?.addEventListener("click", closeLightbox);
    document.getElementById("fa-lightbox-backdrop")?.addEventListener("click", closeLightbox);
    document.getElementById("fa-lightbox-prev")?.addEventListener("click", () => showLightboxImg(lightboxIdx - 1));
    document.getElementById("fa-lightbox-next")?.addEventListener("click", () => showLightboxImg(lightboxIdx + 1));
    document.addEventListener("keydown", e => {
      const lb = document.getElementById("fa-lightbox");
      if (!lb || lb.hidden) return;
      if (e.key === "Escape")     closeLightbox();
      if (e.key === "ArrowLeft")  showLightboxImg(lightboxIdx - 1);
      if (e.key === "ArrowRight") showLightboxImg(lightboxIdx + 1);
    });
  }
  function openLightbox(idx) {
    const lb = document.getElementById("fa-lightbox");
    if (!lb) return;
    lb.hidden = false; document.body.classList.add("fa-overlay-open"); showLightboxImg(idx);
  }
  function closeLightbox() {
    const lb = document.getElementById("fa-lightbox");
    if (!lb) return;
    lb.hidden = true; document.body.classList.remove("fa-overlay-open");
  }
  function showLightboxImg(idx) {
    if (!lightboxPhotos.length) return;
    lightboxIdx = (idx + lightboxPhotos.length) % lightboxPhotos.length;
    const img = document.getElementById("fa-lightbox-img");
    if (img) img.src = lightboxPhotos[lightboxIdx].src;
    const prev = document.getElementById("fa-lightbox-prev");
    const next = document.getElementById("fa-lightbox-next");
    if (prev) prev.style.display = lightboxPhotos.length > 1 ? "" : "none";
    if (next) next.style.display = lightboxPhotos.length > 1 ? "" : "none";
  }

  // ── Review-card lightbox ──────────────────────────────────────────────────
  function bindReviewLightbox() {
    document.getElementById("fa-rlightbox-close")?.addEventListener("click", closeReviewLightbox);
    document.getElementById("fa-rlightbox-backdrop")?.addEventListener("click", closeReviewLightbox);
    document.getElementById("fa-rlightbox-prev")?.addEventListener("click", stepReviewLightbox.bind(null, -1));
    document.getElementById("fa-rlightbox-next")?.addEventListener("click", stepReviewLightbox.bind(null, 1));
    document.addEventListener("keydown", e => {
      const lb = document.getElementById("fa-rlightbox");
      if (!lb || lb.hidden) return;
      if (e.key === "Escape")     closeReviewLightbox();
      if (e.key === "ArrowLeft")  stepReviewLightbox(-1);
      if (e.key === "ArrowRight") stepReviewLightbox(1);
    });
  }
  function openReviewLightbox(groupIdx, photoIdx) {
    const lb = document.getElementById("fa-rlightbox");
    if (!lb || !reviewPhotoGroups.length) return;
    rlGroupIdx = ((groupIdx % reviewPhotoGroups.length) + reviewPhotoGroups.length) % reviewPhotoGroups.length;
    const group = reviewPhotoGroups[rlGroupIdx];
    rlPhotoIdx  = ((photoIdx % group.photos.length) + group.photos.length) % group.photos.length;
    lb.hidden = false; document.body.classList.add("fa-overlay-open"); renderReviewLightbox();
  }
  function closeReviewLightbox() {
    const lb = document.getElementById("fa-rlightbox");
    if (!lb) return;
    // Stop any playing video before hiding
    const videoWrap = document.getElementById("fa-rlightbox-video");
    if (videoWrap) {
      const iframe = videoWrap.querySelector("iframe");
      const video  = videoWrap.querySelector("video");
      if (iframe) iframe.src = ""; // clears iframe src — stops YouTube/Vimeo audio
      if (video)  { video.pause(); video.currentTime = 0; }
    }
    lb.hidden = true; document.body.classList.remove("fa-overlay-open");
  }
  function stepReviewLightbox(direction) {
    if (!reviewPhotoGroups.length) return;
    const group = reviewPhotoGroups[rlGroupIdx];
    const nextPhotoIdx = rlPhotoIdx + direction;
    if (nextPhotoIdx >= 0 && nextPhotoIdx < group.photos.length) {
      rlPhotoIdx = nextPhotoIdx;
    } else {
      rlGroupIdx = ((rlGroupIdx + direction) % reviewPhotoGroups.length + reviewPhotoGroups.length) % reviewPhotoGroups.length;
      const newGroup = reviewPhotoGroups[rlGroupIdx];
      rlPhotoIdx = direction > 0 ? 0 : newGroup.photos.length - 1;
    }
    renderReviewLightbox();
  }
  function renderReviewLightbox() {
    const group = reviewPhotoGroups[rlGroupIdx];
    if (!group) return;
    const r    = group.review;
    const item = group.photos[rlPhotoIdx]; // now {type, src} objects
    const date = r.created_at ? new Date(r.created_at).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }) : "";

    // Swap image/video in the lightbox media area
    const img = document.getElementById("fa-rlightbox-img");
    const videoWrap = document.getElementById("fa-rlightbox-video");
    if (item.type === "video") {
      if (img) img.style.display = "none";
      if (videoWrap) {
        videoWrap.style.cssText = "display:block;position:relative;width:100%;padding-bottom:56.25%;height:0;flex-shrink:0;";
        const isDirectFile = /\.(mp4|webm|mov)(\?|$)/i.test(item.src) || item.src.includes("cdn.shopify.com");
        if (isDirectFile) {
          // Shopify CDN or direct file — render <video> immediately, no poster needed
          videoWrap.innerHTML = `<video class="fa-reviews__video-embed" controls preload="metadata" style="position:absolute;inset:0;width:100%;height:100%;background:#000;"><source src="${esc(item.src)}"></video>`;
        } else {
          // YouTube / Vimeo — show poster, click swaps to iframe inline
          const thumb = getVideoThumbnail(item.src);
          const embedSrc = getYouTubeEmbedUrl(item.src) + "&autoplay=1";
          videoWrap.innerHTML = `
            <div class="fa-reviews__video-poster" data-embed-src="${esc(embedSrc)}" style="position:absolute;inset:0;cursor:pointer;">
              ${thumb ? `<img src="${thumb}" alt="Video thumbnail" style="position:absolute;inset:0;width:100%;height:100%;object-fit:cover;opacity:0.75;">` : `<div style="position:absolute;inset:0;background:#111;"></div>`}
              <div class="fa-reviews__video-play" style="position:relative;z-index:1;">
                <div class="fa-reviews__play-circle">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="#fff" aria-hidden="true"><path d="M8 5v14l11-7z"/></svg>
                </div>
              </div>
            </div>`;
        }
      }
    } else {
      if (img) { img.style.display = ""; img.src = item.src; }
      if (videoWrap) { videoWrap.style.display = "none"; videoWrap.innerHTML = ""; }
    }

    const photoBadge = document.getElementById("fa-rlightbox-photo-badge");
    if (photoBadge) {
      const nPhotos = group.photos.filter(i => i.type === "photo").length;
      const nVideos = group.photos.filter(i => i.type === "video").length;
      const parts = [];
      if (nPhotos) parts.push(`${nPhotos} photo${nPhotos !== 1 ? "s" : ""}`);
      if (nVideos) parts.push(`${nVideos} video${nVideos !== 1 ? "s" : ""}`);
      photoBadge.textContent = parts.join(", ");
    }
    const counter = document.getElementById("fa-rlightbox-photo-counter");
    if (counter) { counter.textContent = group.photos.length > 1 ? `${rlPhotoIdx + 1} / ${group.photos.length}` : ""; counter.style.display = group.photos.length > 1 ? "" : "none"; }
    const reviewCounter = document.getElementById("fa-rlightbox-review-counter");
    if (reviewCounter) { reviewCounter.textContent = reviewPhotoGroups.length > 1 ? `Review ${rlGroupIdx + 1} of ${reviewPhotoGroups.length} with media` : ""; reviewCounter.style.display = reviewPhotoGroups.length > 1 ? "" : "none"; }
    const stars = document.getElementById("fa-rlightbox-stars"); if (stars) stars.innerHTML = starsHTML(r.rating, 16);
    const author = document.getElementById("fa-rlightbox-author"); if (author) author.textContent = r.author_name || "";
    const meta = document.getElementById("fa-rlightbox-meta"); if (meta) meta.textContent = [r.author_location, date].filter(Boolean).join(" · ");
    const title = document.getElementById("fa-rlightbox-title"); if (title) title.textContent = r.title || "";
    const body  = document.getElementById("fa-rlightbox-body");  if (body)  body.textContent  = r.body  || "";
    const thumbs = document.getElementById("fa-rlightbox-thumbs");
    if (thumbs) {
      if (group.photos.length > 1) {
        thumbs.style.display = "";
        thumbs.innerHTML = group.photos.map((it, i) => {
          const thumbSrc = it.type === "video" ? (getVideoThumbnail(it.src) || "") : it.src;
          return `<button class="fa-rlightbox-thumb${i === rlPhotoIdx ? " is-active" : ""}${it.type === "video" ? " fa-rlightbox-thumb--video" : ""}" data-i="${i}" aria-label="${it.type === "video" ? "Video" : "Photo"} ${i + 1}">
            ${thumbSrc ? `<img src="${thumbSrc}" alt="${it.type} ${i + 1}" loading="lazy">` : `<div class="fa-rlightbox-thumb-placeholder"></div>`}
            ${it.type === "video" ? `<span class="fa-rlightbox-play">▶</span>` : ""}
          </button>`;
        }).join("");
        thumbs.querySelectorAll(".fa-rlightbox-thumb").forEach(btn => {
          btn.addEventListener("click", () => { rlPhotoIdx = parseInt(btn.dataset.i); renderReviewLightbox(); });
        });
      } else { thumbs.style.display = "none"; thumbs.innerHTML = ""; }
    }
    const helpfulWrap = document.getElementById("fa-rlightbox-helpful");
    // Color + recommend — between body and helpful row
    const existingExtra = document.getElementById('fa-rlightbox-extra');
    if (existingExtra) existingExtra.remove();
    if (r.color_name || r.recommend) {
      const extra = document.createElement('div');
      extra.id = 'fa-rlightbox-extra';
      extra.style.cssText = 'display:flex; flex-direction:column; gap:6px; padding-top:10px;';
      if (r.color_name) {
        const colorDiv = document.createElement('div');
        colorDiv.style.cssText = 'font-size:12px; color:rgba(0,0,0,0.38);';
        colorDiv.innerHTML = `Color: <span style="color:rgba(0,0,0,0.6);font-weight:500;">${esc(r.color_name)}</span>`;
        extra.appendChild(colorDiv);
      }
      if (r.recommend === 'yes') {
        const recDiv = document.createElement('div');
        recDiv.style.cssText = 'font-size:12px; color:#1a7f3c; font-weight:500;';
        recDiv.textContent = '✓ Recommends this product';
        extra.appendChild(recDiv);
      } else if (r.recommend === 'no') {
        const recDiv = document.createElement('div');
        recDiv.style.cssText = 'font-size:12px; color:rgba(0,0,0,0.4); font-weight:500;';
        recDiv.textContent = '✕ Does not recommend';
        extra.appendChild(recDiv);
      }
      helpfulWrap.parentElement.insertBefore(extra, helpfulWrap);
    }
    if (helpfulWrap) {
      const id = r.id;
      const voteCount = (r.helpful_count || 0) + (votedIds[id] === "yes" ? 1 : 0);
      const isYes = votedIds[id] === "yes"; const isNo = votedIds[id] === "no";
      helpfulWrap.innerHTML = `
        <span class="fa-rlightbox-helpful-label">Was this review helpful?</span>
        <button class="fa-rlightbox-helpful-btn${isYes ? " is-voted" : ""}" data-dir="yes" aria-pressed="${isYes}">👍 Yes (${voteCount})</button>
        <button class="fa-rlightbox-helpful-btn${isNo ? " is-voted" : ""}" data-dir="no" aria-pressed="${isNo}">👎 No</button>`;
      helpfulWrap.querySelectorAll(".fa-rlightbox-helpful-btn").forEach(btn => {
        btn.addEventListener("click", () => handleHelpfulVote(id, btn.dataset.dir));
      });
    }
  }

  // ── Helpful vote ──────────────────────────────────────────────────────────
  function handleHelpfulVote(id, dir) {
    const review   = allReviews.find(r => String(r.id) === String(id));
    const wasVoted = votedIds[id] === dir;
    let apiDir = dir;
    if (wasVoted) apiDir = "unvote_" + dir;
    wasVoted ? delete votedIds[id] : (votedIds[id] = dir);
    saveVotes(); renderList();
    if (!document.getElementById("fa-rlightbox")?.hidden) renderReviewLightbox();
    if (review && review.metaobject_id && dir === "yes") {
      fetch(API_ENDPOINT.replace("/api/reviews", "/api/helpful"), {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ metaobject_id: review.metaobject_id, current_count: review.helpful_count || 0, direction: apiDir }),
      }).catch(err => console.warn("[fa-reviews] helpful vote sync failed:", err));
    }
  }

  // ── Review list ───────────────────────────────────────────────────────────
  function renderList() {
    const list  = document.getElementById("fa-reviews-list");
    const label = document.getElementById("fa-count-label");
    if (!list) return;

    const perPage = parseInt(document.getElementById("fa-reviews")?.dataset.reviewsPerPage || "4");

    let reviews = filterReviews(allReviews, currentFilter);
    reviews = sortReviews(reviews, currentSort);

    const total   = reviews.length;
    const visible = reviews.slice(0, currentPage * perPage);

    if (label) label.textContent = `Showing ${visible.length} of ${total} review${total !== 1 ? "s" : ""}`;

    list.innerHTML = visible.map(reviewCard).join("");

    list.querySelectorAll(".fa-reviews__helpful-btn").forEach(btn => {
      btn.addEventListener("click", () => handleHelpfulVote(btn.dataset.id, btn.dataset.dir));
    });
    list.querySelectorAll(".fa-reviews__flag-btn").forEach(btn => {
      btn.addEventListener("click", () => openFlagModal(btn.dataset.reviewId, btn.dataset.metaobjectId, btn));
    });
    list.querySelectorAll(".fa-reviews__card-photo").forEach(btn => {
      btn.addEventListener("click", () => openReviewLightbox(parseInt(btn.dataset.groupIdx), parseInt(btn.dataset.photoIdx)));
    });

    // Remove any existing load-more button
    document.querySelector(".fa-reviews__load-more-wrap")?.remove();

    // Add load-more button if more reviews remain
    if (visible.length < total) {
      const remaining = total - visible.length;
      const nextBatch = Math.min(perPage, remaining);
      const wrap = document.createElement("div");
      wrap.className = "fa-reviews__load-more-wrap";
      wrap.innerHTML = `
        <button class="fa-reviews__load-more-btn" id="fa-load-more-btn">
          Show ${nextBatch} more review${nextBatch !== 1 ? "s" : ""}
          <span class="fa-reviews__load-more-count">(${remaining} remaining)</span>
        </button>`;
      list.after(wrap);
      wrap.querySelector("#fa-load-more-btn").addEventListener("click", () => {
        currentPage++;
        renderList();
        const cards = list.querySelectorAll(".fa-reviews__card");
        cards[visible.length]?.scrollIntoView({ behavior: "smooth", block: "nearest" });
      });
    }
  }

  function filterReviews(reviews, filter) {
    if (filter === "5")   return reviews.filter(r => Math.floor(r.rating) === 5);
    if (filter === "4")   return reviews.filter(r => Math.floor(r.rating) === 4);
    if (filter === "3")   return reviews.filter(r => Math.floor(r.rating) === 3);
    if (filter === "low") return reviews.filter(r => r.rating <= 2);
    return reviews;
  }
  function sortReviews(reviews, sort) {
    const c = [...reviews];
    if (sort === "helpful") return c.sort((a, b) => b.helpful_count - a.helpful_count);
    if (sort === "newest")  return c.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    if (sort === "highest") return c.sort((a, b) => b.rating - a.rating);
    if (sort === "lowest")  return c.sort((a, b) => a.rating - b.rating);
    return c;
  }

  // ── Video embed — poster that swaps to iframe on click (plays inline) ────
  function videoEmbedHTML(url) {
    if (!url) return "";
    const ytMatch = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/);
    if (ytMatch) {
      const id = ytMatch[1];
      return `<div class="fa-reviews__video-wrap">
        <iframe class="fa-reviews__video-embed"
          src="https://www.youtube-nocookie.com/embed/${id}"
          frameborder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowfullscreen
          loading="lazy">
        </iframe>
      </div>`;
    }
    const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
    if (vimeoMatch) {
      const id = vimeoMatch[1];
      return `<div class="fa-reviews__video-wrap">
        <iframe class="fa-reviews__video-embed"
          src="https://player.vimeo.com/video/${id}"
          frameborder="0"
          allow="autoplay; fullscreen; picture-in-picture"
          allowfullscreen
          loading="lazy">
        </iframe>
      </div>`;
    }
    if (/\.(mp4|webm|mov)(\?|$)/i.test(url) || url.includes("cdn.shopify.com")) {
      return `<div class="fa-reviews__video-wrap">
        <video class="fa-reviews__video-embed" controls preload="metadata">
          <source src="${esc(url)}">
        </video>
      </div>`;
    }
    return `<div class="fa-reviews__video-link">
      <a href="${esc(url)}" target="_blank" rel="noopener noreferrer">▶ Watch video</a>
    </div>`;
  }

  // ── Full-screen video modal (fallback for contexts where inline won't work) 
  function buildVideoModal() {
    if (document.getElementById("fa-video-modal")) return;
    const modal = document.createElement("div");
    modal.id = "fa-video-modal";
    modal.setAttribute("hidden", "");
    modal.innerHTML = `
      <button id="fa-video-modal-close" class="fa-video-modal__close" aria-label="Close video">✕</button>
      <div id="fa-video-modal-inner" class="fa-video-modal__inner"></div>`;
    document.body.appendChild(modal);
    document.getElementById("fa-video-modal-close").addEventListener("click", closeFaVideoModal);
    modal.addEventListener("click", e => { if (e.target === modal) closeFaVideoModal(); });
    document.addEventListener("keydown", e => { if (e.key === "Escape" && !modal.hidden) closeFaVideoModal(); });
  }

  function openFaVideoModal(url) {
    const modal = document.getElementById("fa-video-modal");
    const inner = document.getElementById("fa-video-modal-inner");
    if (!modal || !inner) return;
    // Stop any in-card video that might be playing
    document.querySelectorAll(".fa-reviews__video-embed").forEach(function(el) {
      if (el.tagName === "IFRAME") el.src = el.src; // reload to stop
      if (el.tagName === "VIDEO") { el.pause(); el.currentTime = 0; }
    });
    const ytMatch = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/);
    const src = ytMatch
      ? `https://www.youtube-nocookie.com/embed/${ytMatch[1]}?autoplay=1`
      : url;
    inner.innerHTML = `<iframe src="${src}" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe>`;
    modal.hidden = false;
    document.body.classList.add("fa-overlay-open");
  }

  function closeFaVideoModal() {
    const modal = document.getElementById("fa-video-modal");
    const inner = document.getElementById("fa-video-modal-inner");
    if (!modal) return;
    if (inner) inner.innerHTML = "";
    modal.hidden = true;
    document.body.classList.remove("fa-overlay-open");
  }

  function reviewCard(r) {
    const voteCount = r.helpful_count + (votedIds[r.id] === "yes" ? 1 : 0);
    const isYes = votedIds[r.id] === "yes";
    const isNo  = votedIds[r.id] === "no";
    const date  = r.created_at ? new Date(r.created_at).toLocaleDateString("en-US", { month: "short", year: "numeric" }) : "";

    let cardPhotos = [];
    try {
      const raw = r.photos;
      if (Array.isArray(raw)) cardPhotos = raw.filter(s => typeof s === "string" && s.length > 0);
      else if (typeof raw === "string" && raw.startsWith("[")) cardPhotos = JSON.parse(raw).filter(s => typeof s === "string" && s.length > 0);
    } catch {}

    const groupIdx  = reviewPhotoGroups.findIndex(g => g.review === r);
    const photoHTML = cardPhotos.map((src, i) =>
      `<button class="fa-reviews__card-photo" data-group-idx="${groupIdx}" data-photo-idx="${i}" aria-label="View photo">
        <img src="${src}" alt="Review photo" loading="lazy">
      </button>`).join("");

    const attrs = [
      r.install_method && `<span class="fa-reviews__attr"><b>Install:</b> ${esc(r.install_method)}</span>`,
      r.room_type      && `<span class="fa-reviews__attr"><b>Room:</b> ${esc(r.room_type)}</span>`,
      r.recommend === "yes" && `<span class="fa-reviews__attr fa-reviews__attr--rec">✓ Recommends this product</span>`,
      r.recommend === "no"  && `<span class="fa-reviews__attr fa-reviews__attr--norec">✕ Does not recommend</span>`,
    ].filter(Boolean).join("");

    const liked = r.liked_attributes
      ? r.liked_attributes.split(",").map(c => c.trim()).filter(Boolean)
          .map(c => `<span class="fa-reviews__card-chip">${esc(c)}</span>`).join("")
      : "";

    return `
      <article class="fa-reviews__card" data-rating="${r.rating}">
        <header class="fa-reviews__card-header">
          <div>
            <span class="fa-reviews__author">${esc(r.author_name)}</span>
            ${r.verified_buyer ? `<span class="fa-reviews__badge fa-reviews__badge--verified">✓ Verified</span>` : ""}
            <div class="fa-reviews__meta">${esc(r.author_location)}${r.author_location && date ? " · " : ""}${date}</div>
            ${r.color_name ? `<div class="fa-reviews__color-meta">Color: <span>${esc(r.color_name)}</span></div>` : ""}
          </div>
          <div class="fa-reviews__card-stars" aria-label="${r.rating} out of 5 stars">${starsHTML(r.rating, 14)}</div>
        </header>
        <h3 class="fa-reviews__card-title">${esc(r.title)}</h3>
        <p class="fa-reviews__card-body">${esc(r.body)}</p>
        ${liked ? `<div class="fa-reviews__card-chips">${liked}</div>` : ""}
        ${cardPhotos.length ? `<div class="fa-reviews__card-photos">${photoHTML}</div>` : ""}
        ${r.video_url ? videoEmbedHTML(r.video_url) : ""}
        ${attrs ? `<div class="fa-reviews__attrs">${attrs}</div>` : ""}
        <div class="fa-reviews__helpful">
          <span class="fa-reviews__helpful-label">Helpful?</span>
          <button class="fa-reviews__helpful-btn${isYes ? " is-voted" : ""}" data-id="${r.id}" data-dir="yes" aria-pressed="${isYes}">👍 Yes (${voteCount})</button>
          <button class="fa-reviews__helpful-btn${isNo ? " is-voted" : ""}" data-id="${r.id}" data-dir="no" aria-pressed="${isNo}">👎 No</button>
          <button class="fa-reviews__flag-btn" data-review-id="${r.id}" data-metaobject-id="${r.metaobject_id}" title="Report this review" aria-label="Report this review">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/><line x1="4" y1="22" x2="4" y2="15"/></svg>
            Report
          </button>
        </div>
      </article>`;
  }

  // ── Stars HTML ────────────────────────────────────────────────────────────
  function starsHTML(rating, size) {
    const full = Math.floor(rating);
    const half = (rating % 1) >= 0.5;
    let html = "";
    for (let i = 1; i <= 5; i++) {
      if (i <= full) {
        html += `<svg class="fa-star fa-star--full" width="${size}" height="${size}" viewBox="0 0 20 20" aria-hidden="true"><path d="M10 1l2.39 4.84 5.35.78-3.87 3.77.91 5.32L10 13.27l-4.78 2.44.91-5.32L2.26 6.62l5.35-.78L10 1z"/></svg>`;
      } else if (i === full + 1 && half) {
        const uid = `hs_${size}_${i}_${Math.random().toString(36).slice(2,6)}`;
        html += `<svg class="fa-star fa-star--half" width="${size}" height="${size}" viewBox="0 0 20 20" aria-hidden="true" style="overflow:visible">
          <defs><clipPath id="${uid}"><rect x="0" y="0" width="10" height="20"/></clipPath></defs>
          <path d="M10 1l2.39 4.84 5.35.78-3.87 3.77.91 5.32L10 13.27l-4.78 2.44.91-5.32L2.26 6.62l5.35-.78L10 1z" fill="none" stroke="rgba(0,0,0,0.18)" stroke-width="1.5"/>
          <path d="M10 1l2.39 4.84 5.35.78-3.87 3.77.91 5.32L10 13.27l-4.78 2.44.91-5.32L2.26 6.62l5.35-.78L10 1z" fill="#d22129" clip-path="url(#${uid})"/>
        </svg>`;
      } else {
        html += `<svg class="fa-star fa-star--empty" width="${size}" height="${size}" viewBox="0 0 20 20" aria-hidden="true"><path d="M10 1l2.39 4.84 5.35.78-3.87 3.77.91 5.32L10 13.27l-4.78 2.44.91-5.32L2.26 6.62l5.35-.78L10 1z" fill="none" stroke="rgba(0,0,0,0.18)" stroke-width="1.5"/></svg>`;
      }
    }
    return html;
  }

  function esc(str) {
    return String(str ?? "").replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;").replace(/"/g,"&quot;");
  }

  // ── Filter / sort ─────────────────────────────────────────────────────────
  function bindFilters() {
    document.querySelectorAll(".fa-reviews__filter").forEach(btn => {
      btn.addEventListener("click", () => {
        document.querySelectorAll(".fa-reviews__filter").forEach(b => b.classList.remove("is-active"));
        btn.classList.add("is-active");
        currentFilter = btn.dataset.filter;
        currentPage = 1;
        renderList();
      });
    });
  }
  function bindSort() {
    document.getElementById("fa-sort")?.addEventListener("change", e => { currentSort = e.target.value; currentPage = 1; renderList(); });
  }

  // ── Overlay ───────────────────────────────────────────────────────────────
  function bindWriteButton() { document.getElementById("fa-write-btn")?.addEventListener("click", openOverlay); }
  function openOverlay() {
    const overlay = document.getElementById("fa-overlay");
    if (!overlay) return;
    overlay.hidden = false; document.body.classList.add("fa-overlay-open");
    setTimeout(() => document.getElementById("fa-star-picker")?.querySelector("button")?.focus(), 50);
  }
  function closeOverlay() {
    const overlay = document.getElementById("fa-overlay");
    if (!overlay) return;
    overlay.hidden = true; document.body.classList.remove("fa-overlay-open"); resetForm();
  }
  function bindOverlay() {
    document.getElementById("fa-overlay-close")?.addEventListener("click", closeOverlay);
    document.getElementById("fa-overlay-backdrop")?.addEventListener("click", closeOverlay);
    document.getElementById("fa-cancel")?.addEventListener("click", closeOverlay);
    document.addEventListener("keydown", e => {
      if (e.key === "Escape" && !document.getElementById("fa-overlay")?.hidden) closeOverlay();
    });
  }

  // ── Half-star picker ──────────────────────────────────────────────────────
  function buildHalfStarPicker() {
    const picker = document.getElementById("fa-star-picker");
    if (!picker) return;
    const P = "M10 1l2.39 4.84 5.35.78-3.87 3.77.91 5.32L10 13.27l-4.78 2.44.91-5.32L2.26 6.62l5.35-.78L10 1z";
    let html = "";
    for (let s = 1; s <= 5; s++) {
      html += [
        '<span class="fa-hstar" data-star="' + s + '">',
          '<svg viewBox="0 0 20 20" width="40" height="40" class="fa-hstar-svg" aria-hidden="true">',
            '<defs><clipPath id="fa-hc' + s + '"><rect x="0" y="0" width="10" height="20"/></clipPath></defs>',
            '<path class="fa-hstar-bg"   d="' + P + '" fill="none" stroke="rgba(0,0,0,0.18)" stroke-width="1.5"/>',
            '<path class="fa-hstar-half" d="' + P + '" fill="#d22129" clip-path="url(#fa-hc' + s + ')" style="display:none"/>',
            '<path class="fa-hstar-full" d="' + P + '" fill="#d22129" style="display:none"/>',
          '</svg>',
          '<button type="button" aria-label="' + (s - 0.5) + ' stars" data-val="' + (s - 0.5) + '" class="fa-hstar-btn fa-hstar-left"></button>',
          '<button type="button" aria-label="' + s + ' stars" data-val="' + s + '" class="fa-hstar-btn fa-hstar-right"></button>',
        '</span>'
      ].join("");
    }
    picker.innerHTML = html;

    function paint(val) {
      picker.querySelectorAll(".fa-hstar").forEach(function(star, idx) {
        var n = idx + 1, full = star.querySelector(".fa-hstar-full"), half = star.querySelector(".fa-hstar-half"), bg = star.querySelector(".fa-hstar-bg");
        if (val >= n)            { full.style.display = ""; half.style.display = "none"; bg.setAttribute("stroke", "#d22129"); }
        else if (val >= n - 0.5) { full.style.display = "none"; half.style.display = ""; bg.setAttribute("stroke", "#d22129"); }
        else                     { full.style.display = "none"; half.style.display = "none"; bg.setAttribute("stroke", "rgba(0,0,0,0.18)"); }
      });
    }
    function setLabel(val) {
      var label = document.getElementById("fa-star-label");
      if (label) label.textContent = val ? (HALF_LABELS[Math.round(val * 2)] || "") : "";
    }
    picker.querySelectorAll(".fa-hstar-btn").forEach(function(btn) {
      var val = parseFloat(btn.dataset.val);
      btn.addEventListener("mouseenter", function() { paint(val); setLabel(val); });
      btn.addEventListener("mouseleave", function() { paint(currentRating); setLabel(currentRating); });
      btn.addEventListener("click", function() {
        currentRating = val;
        var hidden = document.getElementById("fa-rating-value"); if (hidden) hidden.value = val;
        paint(val); setLabel(val);
        var err = document.getElementById("fa-rating-error"); if (err) err.hidden = true;
      });
    });

    function getRatingFromTouch(e) {
      const touch = e.touches[0] || e.changedTouches[0];
      const pickerRect = picker.getBoundingClientRect();
      const x = Math.max(0, touch.clientX - pickerRect.left);
      const starWidth = pickerRect.width / 5;
      const starIndex = Math.min(4, Math.floor(x / starWidth));
      const withinStar = x - starIndex * starWidth;
      return starIndex + (withinStar < starWidth / 2 ? 0.5 : 1);
    }
    picker.addEventListener("touchstart", function(e) { e.preventDefault(); var val = getRatingFromTouch(e); paint(val); setLabel(val); }, { passive: false });
    picker.addEventListener("touchmove",  function(e) { e.preventDefault(); var val = getRatingFromTouch(e); paint(val); setLabel(val); }, { passive: false });
    picker.addEventListener("touchend",   function(e) {
      e.preventDefault(); var val = getRatingFromTouch(e); currentRating = val;
      var hidden = document.getElementById("fa-rating-value"); if (hidden) hidden.value = val;
      paint(val); setLabel(val);
      var err = document.getElementById("fa-rating-error"); if (err) err.hidden = true;
    }, { passive: false });
  }

  // ── Recommend ─────────────────────────────────────────────────────────────
  function bindRecommend() {
    document.querySelectorAll(".fa-overlay__recommend-btn").forEach(btn => {
      btn.addEventListener("click", () => {
        document.querySelectorAll(".fa-overlay__recommend-btn").forEach(b => b.classList.remove("is-selected"));
        btn.classList.add("is-selected"); recommendValue = btn.dataset.value;
        const err = document.getElementById("fa-recommend-error"); if (err) err.hidden = true;
      });
    });
  }

  // ── Chips ─────────────────────────────────────────────────────────────────
  function bindChips() {
    document.querySelectorAll(".fa-overlay__chip").forEach(chip => {
      chip.addEventListener("click", () => chip.classList.toggle("is-selected"));
    });
  }
  function getSelectedChips() {
    return [...document.querySelectorAll(".fa-overlay__chip.is-selected")].map(c => c.dataset.value).join(", ");
  }

  // ── Photo upload ──────────────────────────────────────────────────────────
  function bindPhotoUpload() {
    const input = document.getElementById("fa-photo-input");
    const addLabel = document.getElementById("fa-photo-add");
    if (!input) return;
    input.addEventListener("change", () => handlePhotoSelect(input.files));
    addLabel?.addEventListener("dragover", e => { e.preventDefault(); addLabel.style.borderColor = "#d22129"; });
    addLabel?.addEventListener("dragleave", () => { addLabel.style.borderColor = ""; });
    addLabel?.addEventListener("drop", e => { e.preventDefault(); addLabel.style.borderColor = ""; handlePhotoSelect(e.dataTransfer.files); });
  }
  function handlePhotoSelect(files) {
    const remaining = MAX_PHOTOS - selectedPhotos.length;
    Array.from(files).slice(0, remaining).forEach(file => {
      if (!file.type.startsWith("image/")) return;
      if (file.size > 10 * 1024 * 1024) { alert(`${file.name} is too large. Max 10MB.`); return; }
      const reader = new FileReader();
      reader.onload = e => { selectedPhotos.push({ file, dataUrl: e.target.result }); renderPhotoPreview(); };
      reader.readAsDataURL(file);
    });
    document.getElementById("fa-photo-input").value = "";
  }
  function renderPhotoPreview() {
    const container = document.getElementById("fa-photo-previews");
    const addLabel  = document.getElementById("fa-photo-add");
    if (!container) return;
    container.innerHTML = selectedPhotos.map((p, i) => `
      <div class="fa-overlay__photo-thumb">
        <img src="${p.dataUrl}" alt="Photo ${i + 1}">
        <button class="fa-overlay__photo-remove" data-idx="${i}" aria-label="Remove" type="button">✕</button>
      </div>`).join("");
    container.querySelectorAll(".fa-overlay__photo-remove").forEach(btn => {
      btn.addEventListener("click", () => { selectedPhotos.splice(parseInt(btn.dataset.idx), 1); renderPhotoPreview(); });
    });
    if (addLabel) addLabel.style.display = selectedPhotos.length >= MAX_PHOTOS ? "none" : "";
  }

  function bindVideoUpload() {
    const input    = document.getElementById("fa-video-input");
    const addLabel = document.getElementById("fa-video-add");
    if (!input) return;
    input.addEventListener("change", () => {
      const file = input.files?.[0];
      input.value = "";
      if (!file) return;
      if (!file.type.startsWith("video/")) { alert("Please select a video file (MP4, MOV or WebM)."); return; }
      if (file.size > 500 * 1024 * 1024) { alert(`${file.name} is too large. Max 500MB.`); return; }
      selectedVideo = { file, blobUrl: URL.createObjectURL(file), resourceUrl: null, uploading: true };
      renderVideoPreview();
      uploadVideoToStage(file);
    });
    addLabel?.addEventListener("dragover",  e => { e.preventDefault(); addLabel.style.borderColor = "#d22129"; });
    addLabel?.addEventListener("dragleave", () => { addLabel.style.borderColor = ""; });
    addLabel?.addEventListener("drop", e => {
      e.preventDefault(); addLabel.style.borderColor = "";
      const file = e.dataTransfer.files?.[0];
      if (!file) return;
      // Simulate change event
      const dt = new DataTransfer(); dt.items.add(file);
      input.files = dt.files;
      input.dispatchEvent(new Event("change"));
    });
  }

  async function uploadVideoToStage(file) {
    const statusEl = document.getElementById("fa-video-status");
    try {
      // 1. Get staged upload URL from our app
      const stageRes = await fetch(STAGE_VIDEO_ENDPOINT, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ filename: file.name, mimeType: file.type, fileSize: file.size }),
      });
      const stageData = await stageRes.json();
      if (!stageRes.ok || stageData.error) throw new Error(stageData.error || "Stage failed");

      const { url, resourceUrl, parameters } = stageData;

      // 2. Upload directly from browser to S3
      const formData = new FormData();
      parameters.forEach(p => formData.append(p.name, p.value));
      formData.append("file", file);

      const uploadRes = await fetch(url, { method: "POST", body: formData });
      if (!uploadRes.ok) throw new Error(`S3 upload failed: ${uploadRes.status}`);

      // 3. Store resourceUrl — sent with review submission
      selectedVideo.resourceUrl = resourceUrl;
      selectedVideo.uploading   = false;
      console.log("[fa-reviews] Video staged:", resourceUrl);
      renderVideoPreview();
    } catch (err) {
      console.error("[fa-reviews] Video upload error:", err);
      selectedVideo.uploading   = false;
      selectedVideo.uploadError = true;
      renderVideoPreview();
    }
  }

  function renderVideoPreview() {
    const container = document.getElementById("fa-video-preview");
    const addLabel  = document.getElementById("fa-video-add");
    if (!container) return;
    if (!selectedVideo) {
      container.innerHTML = "";
      if (addLabel) addLabel.style.display = "";
      return;
    }
    const statusText = selectedVideo.uploading
      ? `<div class="fa-overlay__video-status">Uploading…</div>`
      : selectedVideo.uploadError
        ? `<div class="fa-overlay__video-status fa-overlay__video-status--error">Upload failed — try again</div>`
        : `<div class="fa-overlay__video-status fa-overlay__video-status--ok">✓ Ready</div>`;

    container.innerHTML = `
      <div class="fa-overlay__photo-thumb fa-overlay__video-thumb">
        <video src="${selectedVideo.blobUrl}" style="width:100%;height:100%;object-fit:cover;" muted playsinline></video>
        <div class="fa-overlay__video-icon" aria-hidden="true">${selectedVideo.uploading ? "⏳" : "▶"}</div>
        <button class="fa-overlay__photo-remove" id="fa-video-remove" aria-label="Remove video" type="button">✕</button>
      </div>
      ${statusText}`;
    document.getElementById("fa-video-remove")?.addEventListener("click", () => {
      if (selectedVideo?.blobUrl) URL.revokeObjectURL(selectedVideo.blobUrl);
      selectedVideo = null;
      renderVideoPreview();
    });
    if (addLabel) addLabel.style.display = "none";
  }

  // ── Form ──────────────────────────────────────────────────────────────────
  function bindForm() {
    document.getElementById("fa-submit")?.addEventListener("click", handleSubmit);
    const bodyEl = document.getElementById("fa-body");
    const rem    = document.getElementById("fa-char-remaining");
    bodyEl?.addEventListener("input", () => { if (rem) rem.textContent = 2000 - bodyEl.value.length; });
  }

  async function handleSubmit() {
    const productId = document.getElementById("fa-reviews")?.dataset.productId;
    const ratingErr = document.getElementById("fa-rating-error");
    const recErr    = document.getElementById("fa-recommend-error");
    const colorErr  = document.getElementById("fa-color-error");
    const submitBtn = document.getElementById("fa-submit");
    const statusEl  = document.getElementById("fa-form-status");
    const nameEl    = document.getElementById("fa-author-name");
    const titleEl   = document.getElementById("fa-title");
    const bodyEl    = document.getElementById("fa-body");

    let valid = true;
    if (!currentRating)  { ratingErr.hidden = false; valid = false; } else { ratingErr.hidden = true; }
    if (!recommendValue) { recErr.hidden = false;    valid = false; } else { recErr.hidden = true; }
    if (!colorValue)     { if (colorErr) colorErr.hidden = false; valid = false; } else { if (colorErr) colorErr.hidden = true; }
    if (!nameEl.value.trim() || !titleEl.value.trim() || !bodyEl.value.trim()) {
      showStatus(statusEl, "error", "Please fill in all required fields."); valid = false;
    }
    if (!valid) return;

    submitBtn.disabled = true; submitBtn.textContent = "Submitting…";
    hideStatus(statusEl);

    // Block submit if video is still uploading to S3
    if (selectedVideo?.uploading) {
      showStatus(statusEl, "error", "Please wait — your video is still uploading.");
      submitBtn.disabled = false; submitBtn.textContent = "Submit review";
      return;
    }

    const photos = await Promise.all(selectedPhotos.map(async p => {
      try { const compressed = await compressImage(p.dataUrl, 800, 0.75); return { name: p.file.name, type: "image/jpeg", dataUrl: compressed }; }
      catch { return { name: p.file.name, type: p.file.type, dataUrl: p.dataUrl }; }
    }));
    if (photos.length) console.log("[fa-reviews] Photos compressed:", photos.map(p => Math.round(p.dataUrl.length/1024)+"KB").join(", "));

    const payload = {
      product_id:          productId,
      author_name:         nameEl.value.trim(),
      author_location:     document.getElementById("fa-location")?.value.trim() ?? "",
      rating:              currentRating,
      title:               titleEl.value.trim(),
      review_body:         bodyEl.value.trim(),
      install_method:      document.getElementById("fa-install")?.value ?? "",
      room_type:           document.getElementById("fa-room")?.value ?? "",
      color_name:          colorValue || "",
      liked_attributes:    getSelectedChips(),
      recommend:           recommendValue,
      video_resource_url:  selectedVideo?.resourceUrl ?? "",  // S3 resourceUrl, not base64
      photos,
    };

    try {
      const res  = await fetch(API_ENDPOINT, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
      const data = await res.json();
      if (data.success) {
        closeOverlay();
        const innerBox = document.querySelector(".fa-reviews__inner-box") || document.querySelector(".fa-reviews");
        if (innerBox) {
          innerBox.querySelectorAll(".fa-reviews__thankyou").forEach(el => el.remove());
          const ty = document.createElement("div");
          ty.className = "fa-reviews__thankyou";
          const msg = data.message || "Thank you for your review!";
          ty.innerHTML = `<span class="fa-reviews__thankyou-icon">✓</span>
            <div>
              <div class="fa-reviews__thankyou-title">${esc(msg)}</div>
              <div class="fa-reviews__thankyou-sub">Reviews are checked before publishing — we appreciate your feedback.</div>
            </div>`;
          const summary = innerBox.querySelector(".fa-reviews__summary");
          if (summary && summary.nextSibling) innerBox.insertBefore(ty, summary.nextSibling);
          else innerBox.appendChild(ty);
          ty.scrollIntoView({ behavior: "smooth", block: "nearest" });
          setTimeout(() => ty.remove(), 10000);
        }
        if (data.metaobjectId) {
          const optimisticReview = {
            id:               Date.now(),
            author_name:      payload.author_name,
            author_location:  payload.author_location,
            rating:           parseFloat(payload.rating),
            title:            payload.title,
            body:             payload.review_body,   // payload uses review_body, card expects body
            install_method:   payload.install_method,
            room_type:        payload.room_type,
            color_name:       payload.color_name,
            liked_attributes: payload.liked_attributes,
            recommend:        payload.recommend,
            video_url:        selectedVideo?.blobUrl ?? "",
            photos:           photos.map(p => p.dataUrl),
            verified_buyer:   false,
            helpful_count:    0,
            created_at:       new Date().toISOString(),
          };
          allReviews.unshift(optimisticReview);
          buildPhotoGallery(); renderList();
        }
      } else {
        showStatus(statusEl, "error", data.error || "Something went wrong. Please try again.");
      }
    } catch {
      showStatus(statusEl, "error", "Unable to submit right now. Please try again later.");
    } finally {
      submitBtn.disabled = false; submitBtn.textContent = "Submit review";
    }
  }

  function resetForm() {
    ["fa-author-name","fa-location","fa-title","fa-body"].forEach(id => { const el = document.getElementById(id); if (el) el.value = ""; });
    document.getElementById("fa-install") && (document.getElementById("fa-install").value = "");
    document.getElementById("fa-room")    && (document.getElementById("fa-room").value    = "");
    currentRating = 0;
    const hidden = document.getElementById("fa-rating-value"); if (hidden) hidden.value = "";
    document.querySelectorAll(".fa-hstar-full, .fa-hstar-half").forEach(p => p.style.display = "none");
    document.querySelectorAll(".fa-hstar-bg").forEach(p => p.setAttribute("stroke", "rgba(0,0,0,0.18)"));
    const sl = document.getElementById("fa-star-label"); if (sl) sl.textContent = "";
    recommendValue = "";
    document.querySelectorAll(".fa-overlay__recommend-btn").forEach(b => b.classList.remove("is-selected"));
    document.querySelectorAll(".fa-overlay__chip").forEach(c => c.classList.remove("is-selected"));
    const rem = document.getElementById("fa-char-remaining"); if (rem) rem.textContent = "2000";
    selectedPhotos = []; renderPhotoPreview();
    document.getElementById("fa-photo-add") && (document.getElementById("fa-photo-add").style.display = "");
    if (selectedVideo?.blobUrl) URL.revokeObjectURL(selectedVideo.blobUrl);
    selectedVideo = null; renderVideoPreview();
    ["fa-rating-error","fa-recommend-error"].forEach(id => { const el = document.getElementById(id); if (el) el.hidden = true; });
    const colorErr = document.getElementById("fa-color-error");
    if (colorErr) colorErr.hidden = true;
    hideStatus(document.getElementById("fa-form-status"));
    // Rebuild color field to reset swatch selection back to default
    buildColorField();
  }

  function showStatus(el, type, msg) {
    if (!el) return;
    el.hidden = false; el.className = `fa-overlay__form-status fa-overlay__form-status--${type}`;
    el.textContent = msg; el.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }
  function hideStatus(el) { if (!el) return; el.hidden = true; el.textContent = ""; }

  // ── Flag modal ────────────────────────────────────────────────────────────
  let flagReviewId = null, flagMetaobjectId = null, flagSourceBtn = null;

  function openFlagModal(reviewId, metaobjectId, sourceBtn) {
    flagReviewId = reviewId; flagMetaobjectId = metaobjectId; flagSourceBtn = sourceBtn;
    const modal = document.getElementById("fa-flag-modal"); if (!modal) return;
    document.querySelectorAll("input[name='fa-flag-reason']").forEach(r => r.checked = false);
    const notes = document.getElementById("fa-flag-notes"); const status = document.getElementById("fa-flag-status");
    if (notes) notes.value = ""; if (status) { status.hidden = true; status.textContent = ""; }
    modal.hidden = false; document.body.classList.add("fa-overlay-open");
  }
  function closeFlagModal() {
    const modal = document.getElementById("fa-flag-modal"); if (!modal) return;
    modal.hidden = true; document.body.classList.remove("fa-overlay-open");
    flagReviewId = flagMetaobjectId = flagSourceBtn = null;
  }
  function bindFlagModal() {
    document.getElementById("fa-flag-close")?.addEventListener("click", closeFlagModal);
    document.getElementById("fa-flag-cancel")?.addEventListener("click", closeFlagModal);
    document.getElementById("fa-flag-backdrop")?.addEventListener("click", closeFlagModal);
    document.getElementById("fa-flag-submit")?.addEventListener("click", submitFlag);
  }
  async function submitFlag() {
    const reasonEl  = document.querySelector("input[name='fa-flag-reason']:checked");
    const status    = document.getElementById("fa-flag-status");
    const submitBtn = document.getElementById("fa-flag-submit");
    if (!reasonEl) {
      if (status) { status.hidden = false; status.className = "fa-flag-modal__status fa-flag-modal__status--error"; status.textContent = "Please select a reason."; }
      return;
    }
    const reason     = reasonEl.value;
    const notes      = document.getElementById("fa-flag-notes")?.value?.trim() || "";
    const fullReason = notes ? `${reason}: ${notes}` : reason;
    submitBtn.disabled = true; submitBtn.textContent = "Submitting…";
    try {
      const res  = await fetch(API_ENDPOINT.replace("/api/reviews", "/api/flag"), {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ review_id: flagReviewId, metaobject_id: flagMetaobjectId, reason: fullReason }),
      });
      const data = await res.json();
      if (data.success) {
        const card = document.querySelector(`[data-review-id="${flagReviewId}"]`)?.closest(".fa-reviews__card");
        if (card) { card.style.opacity = "0.3"; card.style.transition = "opacity 0.4s"; setTimeout(() => card.remove(), 400); }
        if (flagSourceBtn) { flagSourceBtn.innerHTML = "⚑ Reported"; flagSourceBtn.style.color = "#d22129"; flagSourceBtn.disabled = true; }
        closeFlagModal();
      } else {
        if (status) { status.hidden = false; status.className = "fa-flag-modal__status fa-flag-modal__status--error"; status.textContent = data.error || "Something went wrong."; }
      }
    } catch {
      if (status) { status.hidden = false; status.className = "fa-flag-modal__status fa-flag-modal__status--error"; status.textContent = "Unable to submit. Please try again."; }
    } finally {
      submitBtn.disabled = false; submitBtn.textContent = "Submit report";
    }
  }

  // ── Boot ──────────────────────────────────────────────────────────────────
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();